import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiFilm, FiGrid, FiCalendar, FiTrendingUp } from 'react-icons/fi';
import { TopNavbar, NAV_ICON_SEARCH_INDEX, NAV_ICON_FAVORITES_INDEX, NAV_ICON_SETTINGS_INDEX, NAV_ICON_LOCK_INDEX, NAV_ICON_HISTORY_INDEX } from '../../components/TopNavbar/TopNavbar';
import { ContentNav } from '../../components/ContentNav/ContentNav';
import { getRuntimeTmdbLanguage } from '../../api/tmdbAPI';
import { getHighlights } from '../../api/backendAPI';
import { consumeCatalogForceRefresh } from '../../utils/catalogRefresh';
import { useAuth } from '../../contexts/AuthContext';
import { useDevice } from '../../contexts/DeviceContext';
import { useParentalControls } from '../../contexts/ParentalControlsContext';
import { useLockToggle } from '../../hooks/useLockToggle';
import { useContentNavIds } from '../../hooks/useContentNavIds';
import { MovieDetailsEntry } from '../MovieDetails/MovieDetails';
import PasswordModal from '../../components/PasswordModal/PasswordModal';
import HubLoadingMessage from '../../components/HubLoadingMessage/HubLoadingMessage';
import { getCachedVodStreams } from '../../utils/vodStreamsCache';
import { useParentalContentLocks } from '../../hooks/useParentalContentLocks';
import './Movies.css';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface MovieItem {
  id: string;
  title?: string;
  posterUrl?: string;
  backdropUrl?: string;
  vodStreamId?: string;
  categoryId?: string;
}

// ─── Cache ────────────────────────────────────────────────────────────────────

const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

function cacheKey(username: string, language: string) {
  return `ott_movies_v3_${language}_${username}`;
}

function loadCache(username: string, language: string): { slider: MovieItem[]; posters: MovieItem[] } | null {
  try {
    const raw = localStorage.getItem(cacheKey(username, language));
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw) as { data: { slider: MovieItem[]; posters: MovieItem[] }; ts: number };
    if (Date.now() - ts > CACHE_TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
}

function saveCache(username: string, language: string, data: { slider: MovieItem[]; posters: MovieItem[] }) {
  try {
    localStorage.setItem(cacheKey(username, language), JSON.stringify({ data, ts: Date.now() }));
  } catch {}
}

// ─── Category definitions ─────────────────────────────────────────────────────

const CATEGORIES = [
  { id: 'top',     label: 'Top',         Icon: FiTrendingUp, color: '#3d1a5a', accent: '#7b2fbf' },
  { id: 'recent',  label: 'Lançamentos', Icon: FiCalendar, color: '#5a1010', accent: '#8b1a1a' },
  { id: 'cinema',  label: 'Em Cartaz', Icon: FiFilm,    color: '#5a3c00', accent: '#b37000' },
  { id: 'all',     label: 'Todos',    Icon: FiGrid,     color: '#0d3352', accent: '#1565c0' },
] as const;

// ─── Small poster card ────────────────────────────────────────────────────────

interface SmallPosterProps {
  item?: MovieItem;
  loading?: boolean;
  focused?: boolean;
  onClick?: () => void;
}

const SmallPoster: React.FC<SmallPosterProps> = ({ item, loading, focused, onClick }) => {
  const style: React.CSSProperties = item?.posterUrl
    ? { backgroundImage: `url(${item.posterUrl})` }
    : {};
  return (
    <div
      className={[
        'movies__poster',
        loading ? 'movies__poster--loading' : '',
        focused ? 'movies__poster--focused' : '',
      ].filter(Boolean).join(' ')}
      style={style}
      onClick={onClick}
    >
      {item?.title && (
        <div className="movies__poster-title">{item.title}</div>
      )}
    </div>
  );
};

// ─── Category button ──────────────────────────────────────────────────────────

interface CategoryBtnProps {
  label: string;
  Icon: React.ComponentType<{ size?: number }>;
  color: string;
  accent: string;
  focused: boolean;
  onClick?: () => void;
}

const CategoryBtn: React.FC<CategoryBtnProps> = ({ label, Icon, focused, onClick }) => (
  <div
    className={`movies__cat-btn${focused ? ' movies__cat-btn--focused' : ''}`}
    onClick={onClick}
  >
    <Icon size={28} />
    <span className="movies__cat-label">{label}</span>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

interface MoviesProps {
  isActive?: boolean;
  onBack?: () => void;
  onNavigate?: (sectionId: string) => void;
  onOpenMovieDetails?: (entry: MovieDetailsEntry) => void;
  onOpenSettings?: () => void;
  onOpenSearch?: () => void;
  onOpenFavorites?: () => void;
  onOpenHistory?: () => void;
}

type FocusSection = 'topnav' | 'contentnav' | 'slider' | 'categories' | 'posters';
const NAV_ICON_COUNT = 5;

export const Movies: React.FC<MoviesProps> = ({
  isActive = true,
  onBack,
  onNavigate,
  onOpenMovieDetails,
  onOpenSettings,
  onOpenSearch,
  onOpenFavorites,
  onOpenHistory,
}) => {
  const { t, i18n } = useTranslation();
  const { isBlocked } = useParentalControls();
  const {
    handleLockIconClick,
    handleLockPasswordConfirm,
    handleLockPasswordCancel,
    lockModalOpen,
    lockModalMode,
    lockModalError,
  } = useLockToggle();
  const CAT_LABEL_KEY: Record<string, string> = {
    top: 'movies.top',
    recent: 'movies.releases',
    cinema: 'movies.nowPlaying',
    all: 'movies.all',
  };
  const { authData } = useAuth();
  const { isContentRestricted } = useParentalContentLocks();
  const [allSlider, setSlider] = useState<MovieItem[]>([]);
  const [allPosters, setPosters] = useState<MovieItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [sliderIdx, setSliderIdx] = useState(0);
  const [focusSection, setFocusSection] = useState<FocusSection>('slider');
  const [focusCat, setFocusCat] = useState(0);
  const [focusPoster, setFocusPoster] = useState(0);
  const [topnavFocusedIdx, setTopnavFocusedIdx] = useState(0);
  const [contentnavFocusedId, setContentnavFocusedId] = useState('movies');
  const contentNavIds = useContentNavIds();
  const autoAdvRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { deviceId, activationPassword } = useDevice();

  // ─── Data fetching ────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const username = authData?.user?.username ?? 'anon';
    const tmdbLanguage = getRuntimeTmdbLanguage(i18n.language);

    const cached = loadCache(username, tmdbLanguage);
    if (cached) {
      setSlider(cached.slider);
      setPosters(cached.posters);
      setLoading(false);
      return;
    }

    setLoading(true);

    (async () => {
      try {
        const items = await getHighlights(deviceId, activationPassword, tmdbLanguage, consumeCatalogForceRefresh('highlights'));
        if (cancelled) return;

        const vodItems = await getCachedVodStreams().catch(() => []);
        const categoryByStreamId = new Map(vodItems.map((item) => [
          String(item.stream_id || ''),
          String(item.category_id || ''),
        ]));
        const mapped: MovieItem[] = items.map((h) => ({
          id: String(h.tmdbId),
          title: h.title,
          posterUrl: h.posterUrl ?? undefined,
          backdropUrl: h.backdropUrl ?? undefined,
          vodStreamId: h.vodStreamId,
          categoryId: categoryByStreamId.get(String(h.vodStreamId || '')) || undefined,
        }));

        const sliderItems = mapped.slice(0, 5);
        const posterItems = mapped.slice(5, 7);

        setSlider(sliderItems);
        setPosters(posterItems);
        setLoading(false);
          saveCache(username, tmdbLanguage, { slider: sliderItems, posters: posterItems });
      } catch {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [authData, deviceId, activationPassword, i18n.language]);

  const slider = useMemo(
    () => allSlider.filter((item) => !isContentRestricted('movies', item.vodStreamId, item.categoryId)),
    [allSlider, isContentRestricted]
  );
  const posters = useMemo(
    () => allPosters.filter((item) => !isContentRestricted('movies', item.vodStreamId, item.categoryId)),
    [allPosters, isContentRestricted]
  );

  useEffect(() => {
    setSliderIdx((index) => Math.min(index, Math.max(0, slider.length - 1)));
    setFocusPoster((index) => Math.min(index, Math.max(0, posters.length - 1)));
  }, [posters.length, slider.length]);

  // ─── Slider auto-advance ──────────────────────────────────────────────────
  useEffect(() => {
    if (loading || !slider.length) return;
    autoAdvRef.current = setInterval(
      () => setSliderIdx((prev) => (prev + 1) % slider.length),
      5000,
    );
    return () => { if (autoAdvRef.current) clearInterval(autoAdvRef.current); };
  }, [loading, slider.length]);

  // Reset auto-advance timer when user changes slide manually
  const goToSlide = useCallback(
    (idx: number) => {
      if (autoAdvRef.current) { clearInterval(autoAdvRef.current); autoAdvRef.current = null; }
      setSliderIdx(idx);
      if (slider.length) {
        autoAdvRef.current = setInterval(
          () => setSliderIdx((prev) => (prev + 1) % slider.length),
          5000,
        );
      }
    },
    [slider.length],
  );

  // ─── Open details ─────────────────────────────────────────────────────────
  const openItem = useCallback(
    (item?: MovieItem) => {
      if (!item) return;
      onOpenMovieDetails?.({
        tmdbId: Number(item.id),
        title: item.title,
        posterUrl: item.posterUrl,
        backdropUrl: item.backdropUrl,
        vodStreamId: item.vodStreamId,
        categoryId: item.categoryId,
      });
    },
    [onOpenMovieDetails],
  );

  // ─── Keyboard navigation ──────────────────────────────────────────────────
  useEffect(() => {
    if (!isActive) return;

    const onKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
        case 'GoBack':
        case 'BrowserBack':
          e.preventDefault();
          onBack?.();
          break;

        case 'ArrowLeft':
          e.preventDefault();
          if (focusSection === 'topnav') {
            setTopnavFocusedIdx((prev) => Math.max(0, prev - 1));
          } else if (focusSection === 'contentnav') {
            const idx = contentNavIds.indexOf(contentnavFocusedId);
            if (idx > 0) setContentnavFocusedId(contentNavIds[idx - 1]);
          } else if (focusSection === 'slider') {
            goToSlide((sliderIdx - 1 + (slider.length || 1)) % (slider.length || 1));
          } else if (focusSection === 'categories') {
            setFocusCat((prev) => Math.max(0, prev - 1));
          } else if (focusSection === 'posters') {
            if (focusPoster === 0) {
              setFocusSection('categories');
            } else {
              setFocusPoster((prev) => Math.max(0, prev - 1));
            }
          }
          break;

        case 'ArrowRight':
          e.preventDefault();
          if (focusSection === 'topnav') {
            setTopnavFocusedIdx((prev) => Math.min(NAV_ICON_COUNT - 1, prev + 1));
          } else if (focusSection === 'contentnav') {
            const idx = contentNavIds.indexOf(contentnavFocusedId);
            if (idx < contentNavIds.length - 1) setContentnavFocusedId(contentNavIds[idx + 1]);
          } else if (focusSection === 'slider') {
            if (sliderIdx === slider.length - 1) {
              setFocusSection('posters');
              setFocusPoster(0);
            } else {
              goToSlide(sliderIdx + 1);
            }
          } else if (focusSection === 'categories') {
            if (focusCat < CATEGORIES.length - 1) {
              setFocusCat((prev) => prev + 1);
            } else {
              // rightmost category → go to first poster
              setFocusSection('posters');
              setFocusPoster(0);
            }
          } else if (focusSection === 'posters') {
            setFocusPoster((prev) => Math.min(1, prev + 1));
          }
          break;

        case 'ArrowDown':
          e.preventDefault();
          if (focusSection === 'topnav') {
            setFocusSection('contentnav');
          } else if (focusSection === 'contentnav') {
            setFocusSection('slider');
          } else if (focusSection === 'slider') {
            setFocusSection('categories');
            setFocusCat(0);
          }
          break;

        case 'ArrowUp':
          e.preventDefault();
          if (focusSection === 'contentnav') {
            setFocusSection('topnav');
          } else if (focusSection === 'slider') {
            setFocusSection('contentnav');
          } else if (focusSection === 'categories') {
            setFocusSection('slider');
          } else if (focusSection === 'posters') {
            setFocusSection('contentnav');
          }
          break;

        case 'Enter':
        case ' ':
          e.preventDefault();
          if (focusSection === 'topnav') {
            if (topnavFocusedIdx === NAV_ICON_SEARCH_INDEX) onOpenSearch?.();
            if (topnavFocusedIdx === NAV_ICON_FAVORITES_INDEX) onOpenFavorites?.();
            if (topnavFocusedIdx === NAV_ICON_SETTINGS_INDEX) onOpenSettings?.();
            if (topnavFocusedIdx === NAV_ICON_LOCK_INDEX) handleLockIconClick();
            if (topnavFocusedIdx === NAV_ICON_HISTORY_INDEX) onOpenHistory?.();
          } else if (focusSection === 'contentnav') {
            if (contentnavFocusedId === 'movies') {
              setFocusSection('slider');
            } else {
              onNavigate?.(contentnavFocusedId);
            }
          } else if (focusSection === 'slider') {
            openItem(slider[sliderIdx]);
          } else if (focusSection === 'posters') {
            openItem(posters[focusPoster]);
          } else if (focusSection === 'categories') {
            onNavigate?.(`movies-${CATEGORIES[focusCat].id}`);
          }
          break;

        default:
          break;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    isActive, focusSection, focusCat, focusPoster, sliderIdx,
    slider, posters, goToSlide, onBack, openItem, onNavigate,
    topnavFocusedIdx, contentnavFocusedId, contentNavIds, handleLockIconClick, onOpenSearch, onOpenFavorites, onOpenSettings, onOpenHistory,
  ]);

  // ─── Render ───────────────────────────────────────────────────────────────
  const currentSlide = slider[sliderIdx];
  const sliderStyle: React.CSSProperties = currentSlide?.backdropUrl
    ? { backgroundImage: `url(${currentSlide.backdropUrl})` }
    : currentSlide?.posterUrl
    ? { backgroundImage: `url(${currentSlide.posterUrl})` }
    : {};

  if (isBlocked('movies')) {
    return (
      <div className="movies movies--blocked">
        <TopNavbar
          focusedIndex={focusSection === 'topnav' ? topnavFocusedIdx : undefined}
          onIconClick={(i) => {
            if (i === NAV_ICON_SEARCH_INDEX) onOpenSearch?.();
            if (i === NAV_ICON_FAVORITES_INDEX) onOpenFavorites?.();
            if (i === NAV_ICON_SETTINGS_INDEX) onOpenSettings?.();
            if (i === NAV_ICON_LOCK_INDEX) handleLockIconClick();
            if (i === NAV_ICON_HISTORY_INDEX) onOpenHistory?.();
          }}
        />
        <div className="movies__blocked-msg">
          <p>{t('parental.blockedMessage')}</p>
        </div>
        {lockModalOpen && (
          <PasswordModal
            mode={lockModalMode}
            error={lockModalError ? t(lockModalError) : undefined}
            onConfirm={handleLockPasswordConfirm}
            onCancel={handleLockPasswordCancel}
          />
        )}
      </div>
    );
  }

  return (
    <>
    <div className="movies">
      <TopNavbar
        focusedIndex={focusSection === 'topnav' ? topnavFocusedIdx : undefined}
        onIconClick={(i) => {
          if (i === NAV_ICON_SEARCH_INDEX) onOpenSearch?.();
          if (i === NAV_ICON_FAVORITES_INDEX) onOpenFavorites?.();
          if (i === NAV_ICON_SETTINGS_INDEX) onOpenSettings?.();
          if (i === NAV_ICON_LOCK_INDEX) handleLockIconClick();
          if (i === NAV_ICON_HISTORY_INDEX) onOpenHistory?.();
        }}
      />
      <ContentNav
        activeId="movies"
        focusedId={focusSection === 'contentnav' ? contentnavFocusedId : undefined}
        onSelect={(id) => onNavigate?.(id)}
      />

      <div className="movies__main">
        {/* ── Left column ──────────────────────────────────── */}
        <div className="movies__left">
          {/* Slider */}
          <div
            className={[
              'movies__slider',
              loading ? 'movies__slider--loading' : '',
              focusSection === 'slider' ? 'movies__slider--focused' : '',
            ].filter(Boolean).join(' ')}
            style={sliderStyle}
            onClick={() => { setFocusSection('slider'); openItem(currentSlide); }}
          >
            {!loading && currentSlide && (
              <>
                <div className="movies__slider-gradient" />
                <div className="movies__slider-title">{currentSlide.title}</div>
                <div className="movies__slider-dots">
                  {slider.map((_, i) => (
                    <span
                      key={i}
                      className={`movies__slider-dot${i === sliderIdx ? ' movies__slider-dot--active' : ''}`}
                      onClick={(e) => { e.stopPropagation(); goToSlide(i); }}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Category buttons */}
          <div className="movies__categories">
            {CATEGORIES.map((cat, i) => (
              <CategoryBtn
                key={cat.id}
                label={t(CAT_LABEL_KEY[cat.id])}
                Icon={cat.Icon}
                color={cat.color}
                accent={cat.accent}
                focused={focusSection === 'categories' && focusCat === i}
                onClick={() => { setFocusSection('categories'); setFocusCat(i); onNavigate?.(`movies-${cat.id}`); }}
              />
            ))}
          </div>
        </div>

        {/* ── Right column — 2 portrait posters ────────────── */}
        <div className="movies__right">
          {loading ? (
            <>
              <SmallPoster loading />
              <SmallPoster loading />
            </>
          ) : (
            posters.map((item, i) => (
              <SmallPoster
                key={item.id}
                item={item}
                focused={focusSection === 'posters' && focusPoster === i}
                onClick={() => { setFocusSection('posters'); setFocusPoster(i); openItem(item); }}
              />
            ))
          )}
        </div>
      </div>
      <HubLoadingMessage active={loading} scope={`movies:${authData?.user?.username ?? 'anon'}`} />
    </div>
    {lockModalOpen && (
      <PasswordModal
        mode={lockModalMode}
        error={lockModalError ? t(lockModalError) : undefined}
        onConfirm={handleLockPasswordConfirm}
        onCancel={handleLockPasswordCancel}
      />
    )}
    </>
  );
};
